import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["path", "content", "phase"]
  static values = {
    autoStart: { type: Boolean, default: true },
    phaseTransitionDelay: { type: Number, default: 2500 } // Legacy: used if no per-phase duration set
  }

  hasAnimated = false
  currentPhase = 1

  connect() {
    // Skip initialization if animation already completed (e.g., returning to this story)
    if (this.hasAnimated) return

    // Use requestAnimationFrame to ensure SVG is rendered before measuring
    // Mobile Safari needs this to correctly calculate getTotalLength()
    requestAnimationFrame(() => {
      // Calculate and set path lengths for each stroke path
      this.pathTargets.forEach((path) => {
        const length = path.getTotalLength()
        // Fallback to a reasonable default if getTotalLength returns 0 (Safari bug)
        const effectiveLength = length > 0 ? length : 1000
        path.style.strokeDasharray = effectiveLength
        path.style.strokeDashoffset = effectiveLength
      })

      if (this.autoStartValue) {
        // Only auto-start if the element is actually visible
        // (story containers start hidden and are shown via year-in-review controller)
        // Check the parent story container's opacity, not just this element
        const storyContainer = this.element.closest("[data-year-in-review-target='story']")
        const containerOpacity = storyContainer ? getComputedStyle(storyContainer).opacity : "1"
        const isVisible = containerOpacity !== "0"

        if (isVisible) {
          // Wait 100ms then start animations - paths are hidden via CSS opacity until ready
          setTimeout(() => {
            this.animate()
          }, 100)
        }
      }
    })
  }

  animate() {
    // Start the brush stroke animation with individual delays
    this.pathTargets.forEach((path) => {
      const delay = parseInt(path.dataset.delay) || 0
      setTimeout(() => {
        path.style.opacity = "1"
        path.classList.add("animate")
      }, delay)
    })

    // Animate content with staggered delays
    this.contentTargets.forEach((content) => {
      const delay = parseInt(content.dataset.delay) || 0
      const animationType = content.dataset.animation || "default"
      setTimeout(() => {
        content.classList.add("show", `show-${animationType}`)
      }, delay)
    })

    // Handle phase transitions if phases exist
    if (this.hasPhaseTarget) {
      this.currentPhase = 1
      this.scheduleNextPhaseTransition()
    }

    // Mark as animated so reconnects don't reset
    this.hasAnimated = true
  }

  scheduleNextPhaseTransition() {
    const currentPhaseElement = this.getPhaseElement(this.currentPhase)
    if (!currentPhaseElement) return

    // Get duration from current phase's data-phase-duration attribute (in ms)
    // Falls back to legacy phaseTransitionDelayValue for backwards compatibility
    const duration = parseInt(currentPhaseElement.dataset.phaseDuration) || this.phaseTransitionDelayValue

    setTimeout(() => {
      this.transitionToNextPhase()
    }, duration)
  }

  transitionToNextPhase() {
    const nextPhase = this.currentPhase + 1
    const nextPhaseElement = this.getPhaseElement(nextPhase)

    // If there's no next phase, stop
    if (!nextPhaseElement) return

    const currentPhaseElement = this.getPhaseElement(this.currentPhase)

    // Transition out current phase
    if (currentPhaseElement) {
      currentPhaseElement.classList.remove("phase-in")
      currentPhaseElement.classList.add("phase-out")
    }

    // Transition in next phase
    nextPhaseElement.classList.remove("phase-out")
    nextPhaseElement.classList.add("phase-in")

    // Update current phase and schedule next transition
    this.currentPhase = nextPhase
    this.scheduleNextPhaseTransition()
  }

  getPhaseElement(phaseNumber) {
    return this.phaseTargets.find(phase => phase.dataset.phase === String(phaseNumber))
  }

  // Can be called externally to reset and replay animation
  reset() {
    this.hasAnimated = false
    this.currentPhase = 1

    this.pathTargets.forEach((path) => {
      path.classList.remove("animate")
      path.style.opacity = "0"
      // Reset stroke-dashoffset to path length (hidden state)
      const length = path.getTotalLength()
      path.style.strokeDashoffset = length
    })

    this.contentTargets.forEach((content) => {
      // Remove show class and any animation-specific classes
      const animationType = content.dataset.animation || "default"
      content.classList.remove("show", `show-${animationType}`)
    })

    this.phaseTargets.forEach((phase) => {
      phase.classList.remove("phase-in", "phase-out")
    })
  }

  // Replay animation from the beginning
  replay() {
    this.reset()
    // Need to force reflow before re-adding animate class
    void this.element.offsetHeight
    requestAnimationFrame(() => {
      this.animate()
    })
  }
}
